/**
 * A button that is one icon: a sheet's close, a search's clear (DESIGN_SYSTEM.md §5.2). It has no words on
 * screen, so its `label` is REQUIRED: it is what a screen reader says, and the tooltip a host may show.
 * Host-safe: layout on the `<button>`, paint on the inner surface (`PressableSurface`).
 */
import React from "react";

import { Icon, type IconName } from "@rista/ui/library/components/Icon";
import PressableSurface from "@rista/ui/library/components/PressableSurface";
import type { PressState } from "@rista/ui/library/components/PressableSurface";
import { useTheme } from "@rista/ui/library/theme/ThemeProvider";
import { lookFor } from "@rista/ui/library/theme/variants";
import { ICON_BUTTON_GLYPH, iconButtonLook, type IconButtonVariant } from "./look";

export interface IconButtonProps {
  icon: IconName;
  /** what it does, read out (there are no words on screen) */
  label: string;
  onPress: () => void;
  /** `ghost` by default */
  variant?: IconButtonVariant;
  /** `md` (44) by default; `sm` is 36 */
  size?: "sm" | "md";
  disabled?: boolean;
  testID?: string;
}

export function IconButton({ icon, label, onPress, variant, size = "md", disabled, testID }: IconButtonProps) {
  const theme = useTheme();
  const at = (state: PressState) => lookFor(iconButtonLook, theme, { variant, size }, { ...state, disabled: !!disabled });
  return (
    <PressableSurface
      onPress={onPress}
      disabled={disabled}
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={label}
      aria-disabled={!!disabled}
      style={(state) => at(state).root}
      surface={(state) => at(state).surface}
    >
      {(state: PressState) => <Icon name={icon} size={ICON_BUTTON_GLYPH[size]} color={at(state).icon.color as string} />}
    </PressableSurface>
  );
}

export type { IconButtonVariant };
export default IconButton;
